// src/screens/DealershipScreen.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { dealersAPI } from '../services/api';
import VehicleCard from '../components/VehicleCard';
import AppHeader from '../components/AppHeader';
import { MapPin, Phone, Mail, Globe, CheckCircle, Star, Clock } from 'lucide-react';

const DealershipScreen = () => {
  const { id } = useParams();
  const [dealer, setDealer] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await dealersAPI.getDetails(id);
        setDealer(data.dealer);
        setVehicles(data.vehicles);
      } catch (err) {
        console.error('Error fetching dealer details', err);
        setError(err.response?.data?.message || 'Failed to load dealership.');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-app-text-secondary-light dark:text-app-text-secondary text-sm customer-friendly">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-customer-teal border-t-transparent rounded-full animate-spin mb-3"></div>
          <p>Loading dealership...</p>
        </div>
      </div>
    );
  }

  if (error || !dealer) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-app-text-secondary-light dark:text-app-text-secondary text-sm customer-friendly">
        {error || 'Dealership not found.'}
        <Link to="/" className="text-app-accent mt-2">Back to Home</Link>
      </div>
    );
  }

  // Check if dealer is currently open
  const now = new Date();
  const dayOfWeek = now.getDay();
  const currentTime = now.toTimeString().slice(0, 5);
  const todayHours = dealer.hours?.find((h) => h.day === dayOfWeek);
  const isOpen =
    todayHours &&
    todayHours.isOpen &&
    currentTime >= todayHours.open &&
    currentTime <= todayHours.close;

  return (
    <div className="flex flex-col h-full customer-friendly">
      <AppHeader title={dealer.name} subtitle="Dealership" />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-sm">
        {/* Dealer Info Card */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-lg text-app-text-light dark:text-app-text">
                {dealer.name}
              </h2>
              {dealer.verified && (
                <CheckCircle className="w-5 h-5 text-app-success" title="Verified Dealer" />
              )}
            </div>
            {dealer.rating && dealer.rating.count > 0 && (
              <div className="flex items-center gap-1 bg-app-warning/10 px-2 py-1 rounded-lg">
                <Star className="w-3 h-3 fill-app-warning text-app-warning" />
                <span className="text-xs font-semibold text-app-text-light dark:text-app-text">
                  {dealer.rating.average.toFixed(1)}
                </span>
                <span className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">
                  ({dealer.rating.count})
                </span>
              </div>
            )}
          </div>

          {/* Address */}
          {dealer.address && (
            <div className="flex items-start gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                {[
                  dealer.address.street,
                  dealer.address.city,
                  dealer.address.state && dealer.address.zip
                    ? `${dealer.address.state} ${dealer.address.zip}`
                    : dealer.address.state || dealer.address.zip,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>
          )}

          {/* Phone */}
          {dealer.phone && (
            <div className="flex items-center gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              <Phone className="w-4 h-4" />
              <a
                href={`tel:${dealer.phone}`}
                className="hover:text-customer-teal transition-colors"
              >
                {dealer.phone}
              </a>
            </div>
          )}

          {/* Email */}
          {dealer.email && (
            <div className="flex items-center gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              <Mail className="w-4 h-4" />
              <a
                href={`mailto:${dealer.email}`}
                className="hover:text-customer-teal transition-colors"
              >
                {dealer.email}
              </a>
            </div>
          )}

          {/* Website */}
          {dealer.website && (
            <div className="flex items-center gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              <Globe className="w-4 h-4" />
              <a
                href={dealer.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-customer-teal transition-colors"
              >
                {dealer.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}

          {/* Hours */}
          {dealer.hours && dealer.hours.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <Clock
                className={`w-4 h-4 ${isOpen ? 'text-app-success' : 'text-app-error'}`}
              />
              <span
                className={`font-medium ${isOpen ? 'text-app-success' : 'text-app-error'}`}
              >
                {isOpen ? 'Open Now' : 'Closed'}
              </span>
              {todayHours && todayHours.isOpen && (
                <span className="text-app-text-secondary-light dark:text-app-text-secondary">
                  ({todayHours.open} - {todayHours.close})
                </span>
              )}
            </div>
          )}
        </div>

        {/* Inventory Section */}
        <div>
          <h3 className="font-semibold text-base mb-3 text-app-text-light dark:text-app-text">
            Inventory ({vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''})
          </h3>
          {vehicles.length === 0 ? (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              No vehicles available at this dealership right now.
            </div>
          ) : (
            <div className="space-y-3">
              {vehicles.map((v) => (
                <VehicleCard key={v._id} vehicle={v} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealershipScreen;
